import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { authService } from './authService';

// Base URL dos backends (ajuste conforme seu ambiente)
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Criar instância do axios
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor de Request - Adiciona token automaticamente
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = authService.getToken();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de Response - Refresh token automático em 401
let isRefreshing = false;
type Subscriber = { resolve: (token: string) => void; reject: (err: unknown) => void };
let refreshSubscribers: Subscriber[] = [];

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(sub => sub.resolve(token));
  refreshSubscribers = [];
};

const onRefreshFailed = (error: unknown) => {
  refreshSubscribers.forEach(sub => sub.reject(error));
  refreshSubscribers = [];
};

const redirectToLogin = () => {
  // Limpar tokens SINCRONAMENTE antes do redirect para evitar loop de autenticação
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  window.dispatchEvent(new Event('auth-change'));
  // Revogar refresh token no servidor (cookie enviado automaticamente)
  axios.post(`${API_BASE_URL}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
  window.location.replace('/login');
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const isAuthPage = ['/login', '/register'].includes(window.location.pathname);
      if (isAuthPage) return Promise.reject(error);

      // Se não há access token, usuário nunca logou — não tenta refresh
      if (!localStorage.getItem('auth_token')) {
        redirectToLogin();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Enfileira a requisição — será retentada após o refresh concluir
        return new Promise((resolve, reject) => {
          refreshSubscribers.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
        const newAccessToken = data.tokens?.accessToken || data.token;

        if (!newAccessToken) {
          throw new Error('Refresh response missing access token');
        }

        authService.setToken(newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        onRefreshed(newAccessToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Rejeita todos os requests enfileirados e redireciona para login
        onRefreshFailed(refreshError);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient };